import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import type { Gender } from '@yadviga/shared'
import { api } from '../api/client'
import { useSession } from '../store/user.store'
import { Button } from '../components/ui/Button'
import { DateField } from '../components/ui/DatePicker'
import { pickGendered } from '../hooks/use-gender-text'

type Step = 0 | 1 | 2 | 3 | 4
const TOTAL_STEPS = 5

export function OnboardingRoute() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useSession((s) => s.user)
  const setSession = useSession((s) => s.setSession)
  const jwt = useSession((s) => s.jwt)

  const [step, setStep] = useState<Step>(0)
  const [name, setName] = useState(user?.name ?? '')
  const [gender, setGender] = useState<Gender | null>(user?.gender ?? null)
  const [birthDate, setBirthDate] = useState(user?.birthDate ?? '')
  const [direction, setDirection] = useState<1 | -1>(1)

  const saveProfile = useMutation({
    mutationFn: () =>
      api.updateProfile({
        name: name.trim(),
        gender: gender ?? undefined,
        birthDate,
      }),
    onSuccess: (updated) => {
      if (jwt) setSession({ user: updated, jwt })
      goNext()
    },
  })

  function goNext() {
    setDirection(1)
    setStep((s) => Math.min(TOTAL_STEPS - 1, s + 1) as Step)
  }
  function goBack() {
    setDirection(-1)
    setStep((s) => Math.max(0, s - 1) as Step)
  }

  // Greeting on the final step adapts to the user's chosen gender.
  const readyWord = pickGendered(gender, { f: 'Готова', m: 'Готов', n: 'Можно' })

  return (
    <div className="relative flex min-h-dvh flex-col bg-cream">
      <BackgroundGlow />

      {/* Progress dots */}
      <div className="relative z-10 flex justify-center gap-2 px-6 pt-6">
        {Array.from({ length: TOTAL_STEPS }).map((_v, i) => (
          <span
            key={i}
            className={`h-1.5 rounded-full transition-all ${
              i === step ? 'w-8 bg-sage-600' : i < step ? 'w-4 bg-sage-400' : 'w-4 bg-sage-200'
            }`}
          />
        ))}
      </div>

      {/* Step body */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-6 py-6">
        <AnimatePresence mode="wait" custom={direction}>
          {step === 0 && (
            <StepWrap key="0" direction={direction}>
              <CharacterImage src="/characters/yadviga_cozy.webp" alt="Ядвига" />
              <h1 className="font-serif text-3xl text-sage-800">Привет</h1>
              <p className="max-w-xs text-sage-700">
                Меня зовут Ядвига. Я ведьма-травница и компаньон по картам и звёздам. Давай знакомиться.
              </p>
              <Button size="lg" onClick={goNext}>{t('actions.continue')}</Button>
            </StepWrap>
          )}

          {step === 1 && (
            <StepWrap key="1" direction={direction}>
              <CharacterImage src="/characters/yadviga_default.webp" alt="Ядвига" />
              <h2 className="font-serif text-3xl text-sage-800">Как к тебе обращаться?</h2>
              <p className="max-w-xs text-sm text-sage-600">
                Это нужно, чтобы я могла подбирать слова с уважением.
              </p>
              <div className="grid w-full max-w-xs grid-cols-3 gap-2">
                <GenderButton value="female" label="Она" current={gender} onClick={setGender} />
                <GenderButton value="male" label="Он" current={gender} onClick={setGender} />
                <GenderButton value="other" label="Иначе" current={gender} onClick={setGender} />
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={goBack}>{t('actions.back')}</Button>
                <Button onClick={goNext} disabled={gender === null}>
                  {t('actions.continue')}
                </Button>
              </div>
            </StepWrap>
          )}

          {step === 2 && (
            <StepWrap key="2" direction={direction}>
              <CharacterImage src="/characters/yadviga_hands_on_heart.webp" alt="Ядвига" />
              <h2 className="font-serif text-3xl text-sage-800">{t('onboarding.step1_title')}</h2>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('onboarding.step1_placeholder')}
                className="w-full max-w-xs rounded-2xl border border-sage-200 bg-white px-4 py-3 text-center text-lg text-sage-800 outline-none focus:border-sage-500"
                autoFocus
              />
              <div className="flex gap-2">
                <Button variant="secondary" onClick={goBack}>{t('actions.back')}</Button>
                <Button onClick={goNext} disabled={name.trim().length < 1}>
                  {t('actions.continue')}
                </Button>
              </div>
            </StepWrap>
          )}

          {step === 3 && (
            <StepWrap key="3" direction={direction}>
              <CharacterImage src="/characters/yadviga_mystical_reading.webp" alt="Ядвига" />
              <h2 className="font-serif text-3xl text-sage-800">
                {bornQuestion(name, gender)}
              </h2>
              <p className="max-w-xs text-sm text-sage-600">{t('onboarding.step2_hint')}</p>
              <DateField
                value={birthDate}
                onChange={setBirthDate}
                className="w-full max-w-xs justify-center text-center text-lg"
                minAge={0}
                maxAge={100}
              />
              {saveProfile.error && (
                <p className="text-sm text-copper">{(saveProfile.error as Error).message}</p>
              )}
              <div className="flex gap-2">
                <Button variant="secondary" onClick={goBack}>{t('actions.back')}</Button>
                <Button
                  onClick={() => saveProfile.mutate()}
                  loading={saveProfile.isPending}
                  disabled={!/^\d{4}-\d{2}-\d{2}$/.test(birthDate)}
                >
                  {t('actions.save')}
                </Button>
              </div>
            </StepWrap>
          )}

          {step === 4 && (
            <StepWrap key="4" direction={direction}>
              <CharacterImage src="/characters/yadviga_excited.webp" alt="Ядвига" />
              <h2 className="font-serif text-3xl text-sage-800">
                {readyWord}{name ? `, ${name}` : ''}!
              </h2>
              <p className="max-w-xs text-sage-700">{t('onboarding.step3_text')}</p>
              <TotemRow />
              <Button size="lg" onClick={() => navigate('/')}>
                {t('actions.done')}
              </Button>
            </StepWrap>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

// ----- helpers -----

function bornQuestion(name: string, gender: Gender | null): string {
  if (gender === 'female') return name ? `Когда ты родилась, ${name}?` : 'Когда ты родилась?'
  if (gender === 'male') return name ? `Когда ты родился, ${name}?` : 'Когда ты родился?'
  return name ? `Когда у тебя день рождения, ${name}?` : 'Когда у тебя день рождения?'
}

function GenderButton({
  value,
  label,
  current,
  onClick,
}: {
  value: Gender
  label: string
  current: Gender | null
  onClick: (g: Gender) => void
}) {
  const active = current === value
  return (
    <button
      onClick={() => onClick(value)}
      className={`rounded-2xl px-3 py-3 text-sm font-medium transition ${
        active
          ? 'bg-sage-600 text-cream shadow-sm'
          : 'bg-white text-sage-700 ring-1 ring-sage-200 hover:bg-sage-50'
      }`}
    >
      {label}
    </button>
  )
}

function StepWrap({
  children,
  direction,
}: {
  children: React.ReactNode
  direction: 1 | -1
}) {
  return (
    <motion.section
      custom={direction}
      initial={{ opacity: 0, x: direction * 32 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -direction * 32 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      className="flex w-full max-w-sm flex-col items-center gap-5 text-center"
    >
      {children}
    </motion.section>
  )
}

function CharacterImage({ src, alt }: { src: string; alt: string }) {
  // All character webps are pre-normalised to 512×768 with the head anchored
  // at the top of the canvas (sharp fit:cover + position:'top'), so a fixed
  // height + width:auto here gives a uniform footprint across onboarding
  // steps regardless of the original pose's aspect ratio.
  return (
    <motion.div
      initial={{ scale: 0.92, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ delay: 0.05, duration: 0.4, type: 'spring', damping: 18 }}
    >
      <img
        src={src}
        alt={alt}
        className="h-56 w-auto select-none drop-shadow-[0_6px_14px_rgba(52,68,43,0.18)]"
        onError={hideOnError}
        draggable={false}
      />
    </motion.div>
  )
}

function TotemRow() {
  const totems = [
    { src: '/characters/totem_cat.png', label: 'кот' },
    { src: '/characters/totem_owl.png', label: 'сова' },
    { src: '/characters/totem_rat.png', label: 'крыса' },
  ]
  return (
    <div className="flex items-end justify-center gap-3">
      {totems.map((tt, i) => (
        <motion.div
          key={tt.src}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 + i * 0.1, duration: 0.4 }}
          className="flex flex-col items-center"
        >
          <img src={tt.src} alt={tt.label} className="h-20 w-20 object-contain mix-blend-multiply" onError={hideOnError} />
          <span className="text-[11px] text-sage-500">{tt.label}</span>
        </motion.div>
      ))}
    </div>
  )
}

function BackgroundGlow() {
  return (
    <>
      <div className="pointer-events-none absolute inset-x-0 top-0 -z-0 h-72 bg-gradient-to-b from-blush/40 via-cream to-cream" />
      <Sparkles />
    </>
  )
}

function Sparkles() {
  const dots = [
    { left: '8%', top: '18%', delay: 0 },
    { left: '88%', top: '22%', delay: 0.4 },
    { left: '15%', top: '70%', delay: 0.8 },
    { left: '82%', top: '78%', delay: 1.2 },
    { left: '50%', top: '12%', delay: 0.6 },
  ]
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {dots.map((d, i) => (
        <motion.span
          key={i}
          className="absolute text-xs text-copper/70"
          style={{ left: d.left, top: d.top }}
          animate={{ opacity: [0.2, 0.8, 0.2], y: [0, -6, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, delay: d.delay }}
        >
          ✦
        </motion.span>
      ))}
    </div>
  )
}

function hideOnError(e: React.SyntheticEvent<HTMLImageElement>) {
  ;(e.currentTarget as HTMLImageElement).style.display = 'none'
}
